import { useEffect, useRef, useState } from 'react';
import { matchPath, NavLink, useLocation } from 'react-router-dom';
import { appRouteRegistry, getRoutePath, ROUTE_IDS } from '@/app/router/route-registry';
import { useTenant } from '@/tenant/tenant-provider';
import { useSession } from '@/app/providers/session-provider';
import { AssetIcon } from '../../shared/assets';
import TenantNotifications from '../../modules/notifications/TenantNotifications';

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

export default function AppHeader() {
  const { themeMode, toggleTheme } = useTenant();
  const { currentUser, isAuthenticated, canAccessRouteId, logout } = useSession();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const userMenuRef = useRef(null);
  const settingsPath = getRoutePath(ROUTE_IDS.SETTINGS);
  const canAccessSettings = canAccessRouteId(ROUTE_IDS.SETTINGS);
  const accountName = currentUser?.name || 'Sessione assente';
  const accountEmail = currentUser?.email || 'Nessun utente attivo';
  const currentRoute = appRouteRegistry.find((route) =>
    matchPath({ path: route.path, end: true }, location.pathname)
  );
  const routeIcon =
    currentRoute?.iconKey ||
    appRouteRegistry.find(
      (route) => route.iconKey && route.path !== '/' && location.pathname.startsWith(route.path + '/')
    )?.iconKey ||
    'nav.dashboard';
  const routeTitle = currentRoute?.header?.title || currentRoute?.label || 'Webtika';
  const routeSubtitle = currentRoute?.header?.subtitle || '';
  const accountAriaLabel = isAuthenticated ? 'Apri menu utente' : 'Sessione assente';
  const initials = getInitials(accountName) || '?';

  useEffect(() => {
    function closeOnOutsideClick(event) {
      if (!userMenuRef.current?.contains(event.target)) {
        setIsUserMenuOpen(false);
        setIsNotificationsOpen(false);
      }
    }

    function closeOnEscape(event) {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
        setIsNotificationsOpen(false);
      }
    }

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  useEffect(() => { setIsUserMenuOpen(false); setIsNotificationsOpen(false); }, [location.pathname, currentUser?.id, currentUser?.tenantId]);

  function handleLogout() {
    setIsUserMenuOpen(false);
    logout();
  }

  function handleToggleTheme() {
    toggleTheme();
  }

  return (
    <header className="app-header">
      <div className="app-header__route-identity">
        <AssetIcon name={routeIcon} className="app-header__route-icon" size={32} />
        <div className="app-header__route-copy">
          <h1 className="app-header__route-title">{routeTitle}</h1>
          {routeSubtitle ? <p className="app-header__route-subtitle">{routeSubtitle}</p> : null}
        </div>
      </div>

      <div className="app-header__actions" ref={userMenuRef}>
        {isAuthenticated && <TenantNotifications
          key={`${currentUser?.tenantId}:${currentUser?.id}`}
          enabled={Boolean(currentUser?.id)} open={isNotificationsOpen}
          onOpenChange={open => { setIsNotificationsOpen(open); if (open) setIsUserMenuOpen(false); }}
        />}
        <button
          type="button"
          className="app-header__user-pill"
          onClick={() => { setIsUserMenuOpen((currentValue) => !currentValue); setIsNotificationsOpen(false); }}
          aria-label={accountAriaLabel}
          aria-haspopup="menu"
          aria-expanded={isUserMenuOpen}
        >
          <span className="app-header__account-avatar">{initials}</span>
        </button>

        <div
          className={isUserMenuOpen ? 'app-header__user-menu is-open' : 'app-header__user-menu'}
          role="menu"
        >
          <div className="app-header__user-menu-header">
            <span className="app-header__user-menu-name">{accountName}</span>
            <span className="app-header__user-menu-email">{accountEmail}</span>
          </div>
          <button
            type="button"
            className="app-header__user-menu-item"
            onClick={handleToggleTheme}
            role="menuitem"
          >
            <AssetIcon name="actions.theme" size={16} />
            <span>Modalità notturna</span>
            <span
              className={themeMode === 'dark' ? 'app-header__switch is-on' : 'app-header__switch'}
              aria-hidden="true"
            />
          </button>
          {isAuthenticated && canAccessSettings ? (
            <NavLink
              to={settingsPath}
              className="app-header__user-menu-item"
              onClick={() => setIsUserMenuOpen(false)}
              role="menuitem"
            >
              <AssetIcon name="nav.settings" size={16} />
              <span>Impostazioni</span>
            </NavLink>
          ) : null}
          {isAuthenticated ? (
            <button
              type="button"
              className="app-header__user-menu-item is-danger"
              onClick={handleLogout}
              role="menuitem"
            >
              <AssetIcon name="actions.logout" size={16} />
              <span>Logout</span>
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}
